/**
 * The row of owner cards.
 *
 * Every card is a FILTER, and clicking your own name is the point of the row (Theresa, Sep 24
 * 2026): the board is worked through by one person at a time and there was no way to see just
 * your own. Clicking the active card clears it again, so the filter is always reachable and
 * always escapable from the same place.
 *
 * Each card is a real `<button>` with `aria-pressed`, not a div with a click handler — these are
 * the page's primary filter and Tab has to reach them.
 */
import type { FeedbackReport, OwnerId, TriageOwner } from '../../services/feedbackTriageApi';
import { Avatar } from './triageUi';
import { RADIUS } from './triageTokens';

const CARD = `flex min-w-[220px] flex-1 items-center gap-3 border p-3 text-left ${RADIUS}`;

interface Props {
  owners: TriageOwner[];
  reports: FeedbackReport[];
  ownerFilter: 'all' | OwnerId;
  /** The signed-in address, lowercased — whichever card matches it is marked "you". */
  viewerEmail: string;
  onPickOwner: (id: OwnerId) => void;
}

export function OwnerCards({
  owners,
  reports,
  ownerFilter,
  viewerEmail,
  onPickOwner,
}: Props) {
  return (
    <section aria-label="Workload by owner" className="flex flex-wrap gap-3">
      {owners.map((o) => {
        const mine = reports.filter((r) => r.owner === o.id);
        const progress = mine.filter((r) => r.status === 'progress').length;
        const waiting = mine.filter((r) => r.status === 'waiting').length;
        const on = ownerFilter === o.id;
        const isViewer = !!viewerEmail && o.email.toLowerCase() === viewerEmail;

        return (
          <button
            key={o.id}
            type="button"
            aria-pressed={on}
            onClick={() => onPickOwner(o.id)}
            className={`${CARD} bg-white ${
              on
                ? 'border-zinc-900 ring-1 ring-zinc-900'
                : 'border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50'
            }`}
          >
            <Avatar owner={o.id} initials={o.initials} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold text-zinc-900">
                {o.name}
                {isViewer && <span className="font-normal text-zinc-400"> · you</span>}
              </p>
              <p className="text-[11.5px] text-zinc-500">
                {on ? 'showing only these' : `${progress} in progress · ${waiting} waiting`}
              </p>
            </div>
            <span className="text-xl font-bold tracking-tight text-zinc-900">{mine.length}</span>
          </button>
        );
      })}
    </section>
  );
}
